import React, { memo, useState } from "react";
import { TransactionMessage, TransactionMessageProcessingStatus, TransactionMessageReference } from "../../../../app/data/transaction-monitor/models";
import SlideOutSidebar from "../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Collapsible from "../../../../ui-components/collapsible/collapsible";
import "../../transactionMonitor.scss";
import TransactionMessageErrorDetails from "./transactionMesageErrorDetails";
import TransactionMessageData from "./transactionMesageData";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { useDispatch, useSelector } from "react-redux";
import { TransactionMessageReprocessState } from "../../../../slices/transaction-monitor/transaction-messages/TransactionMessagesState";
import { submitMessageForReprocessing, transactionMessageReprocessSelector } from "../../../../slices/transaction-monitor/transaction-messages/transactionMessageReprocessSlice";
import Loading from "../../../../ui-components/loading/loading";
import ConfirmationModal from "../../../../ui-components/confirmation-modal/confirmationModal";
import { toast } from "react-toastify";
import moment from "moment";

export interface TransactionMessageDetailsProps {
    transactionMessage: TransactionMessage,
    show: boolean,
    onClose: () => void;
}

const TransactionMessageDetails: React.FC<TransactionMessageDetailsProps> = memo((props) => {
    const dispatch = useDispatch();
    const [isShowReprocessConfirmation, setIsShowReprocessConfirmation] = useState<boolean>(false);
    const transactionMessageReprocessState: TransactionMessageReprocessState = useSelector(transactionMessageReprocessSelector);

    const reprocessMessage = () => {
        dispatch(submitMessageForReprocessing(
            {
                scopeId: props.transactionMessage.scope.id,
                messageTypeId: props.transactionMessage.types[props.transactionMessage.types.length - 1].typeName.id,
                references: props.transactionMessage.references
            },
            () => { toast.info("Message has been submitted for reprocessing successfully!") },
            (error) => { toast.error(error) },
            () => { setIsShowReprocessConfirmation(false) }
        ));
    }

    return (
        <SlideOutSidebar
            header={`Message Details`}
            show={props.show}
            size="medium"
            onClose={props.onClose}
        >
            <div className="xgs-transaction-monitor__messgae-details">
                <div
                    className="xgs-transaction-monitor__messgae-details__header">
                    <div
                        className="actions">
                        <Button
                            theme={ButtonThemes.blue}
                            disabled={transactionMessageReprocessState.loading}
                            spinner={false}
                            onClick={() => { setIsShowReprocessConfirmation(true) }}
                            className="reprocess-message-button"
                        >
                            Reprocess Message {transactionMessageReprocessState.loading && <Loading isLoading={true}></Loading>}
                        </Button>
                    </div>
                    <div className="info">
                        <div className="detail-group--1">
                            <div className="info__item">
                                <span className="info__item__title">Scope: </span>
                                {props.transactionMessage?.scope?.name}
                            </div>
                            <div className="info__item">
                                <span className="info__item__title">Message Type: </span>
                                {(props.transactionMessage.types && props.transactionMessage.types.length)
                                    ? props.transactionMessage.types[props.transactionMessage.types.length - 1].typeName.name
                                    : ""}
                            </div>
                            <div className="info__item">
                                <span className="info__item__title">Flow: </span>
                                {props.transactionMessage?.flow?.name}
                            </div>
                            <div className="info__item">
                                <span className="info__item__title">Created At: </span>
                                {moment(props.transactionMessage?.createdAt).format("MM/DD/YY HH:mm:ss")}
                            </div>
                            <div className="info__item">
                                <span className="info__item__title">Create By: </span>
                                {props.transactionMessage?.createdBy}
                            </div>
                        </div>
                        <div className="detail-group--2">
                            {
                                !!(
                                    props.transactionMessage
                                    && props.transactionMessage.references
                                    && props.transactionMessage.references.length
                                ) && props.transactionMessage.references.map((reference: TransactionMessageReference, refIdx: number) => (
                                    <div
                                        id={`refIdx_${refIdx}`}
                                        className="info__item">
                                        <span className="info__item__title">{reference.referenceTypeText} </span>
                                        {reference.reference}
                                    </div>
                                ))

                            }
                        </div>
                    </div>
                </div>

                {!!(props.transactionMessage?.status?.processingStatus !== TransactionMessageProcessingStatus.SUCCESS) &&
                    <Collapsible
                        header={<div className="details-header">Error Details</div>}
                        mix={"xgs-transaction-monitor__messgae-details__info-window"}
                        defaultOpened={true}
                    >
                        <TransactionMessageErrorDetails
                            transactionMessage={props.transactionMessage}
                        />
                    </Collapsible>
                }
                <Collapsible
                    header={<div className="details-header">Message Data</div>}
                    mix={"xgs-transaction-monitor__messgae-details__info-window"}
                    defaultOpened={!!(props.transactionMessage?.status?.processingStatus !== TransactionMessageProcessingStatus.ERROR)}
                >
                    <TransactionMessageData
                        transactionMessage={props.transactionMessage}
                    />
                </Collapsible>
            </div>
            <ConfirmationModal
                opened={isShowReprocessConfirmation}
                header="Reprocess Message"
                confirmButtonText="Submit"
                spinner={transactionMessageReprocessState.loading}
                onCancel={() => { setIsShowReprocessConfirmation(false) }}
                onConfirm={() => { reprocessMessage(); }}
            >
                This action will submit the message for reprocessing, are you sure you want to proceed?
            </ConfirmationModal>
        </SlideOutSidebar >
    );
});

export default TransactionMessageDetails;
